import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '@parentpath/i18n';
import { createResetPasswordSchema, type ResetPasswordFormData } from '../schemas/authSchemas';

export function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { t } = useTranslation('auth');
  const resetPasswordSchema = useMemo(() => createResetPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const { error } = await resetPassword(data.email);

      if (error) {
        setError(error.message);
        return;
      }

      setSubmittedEmail(data.email);
      setEmailSent(true);
    } catch {
      setError(t('common.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success message after email is sent
  if (emailSent) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('resetPassword.emailSentTitle')}
              </h2>
              <p className="text-white/70 mb-2">
                {t('resetPassword.emailSentBefore')}
              </p>
              <p className="font-medium text-white mb-4">
                {submittedEmail}
              </p>
              <p className="text-white/70 mb-6">
                {t('resetPassword.emailSentAfter')}
              </p>
              <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-[10px] p-4 mb-6">
                <p className="text-sm text-white/80">
                  <strong className="text-white">{t('register.spamTipPrefix')}</strong> {t('register.spamTipText')}
                </p>
              </div>
              <Link to="/login">
                <button className="w-full py-3 glass-effect border border-white/25 text-white font-medium rounded-[10px] hover:bg-white/10 transition-colors">
                  {t('resetPassword.backToLogin')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://parentpath.de/pp_logo-piktogram-w100.svg"
            alt="ParentPath"
            className="w-16 h-16"
          />
        </div>

        <h1 className="text-center text-2xl font-bold text-white">
          {t('resetPassword.title')}
        </h1>
        <p className="mt-2 text-center text-sm text-white/70">
          {t('resetPassword.description')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-[10px] flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('common.emailLabel')}
              </label>
              <div className="relative">
                <input
                  {...register('email')}
                  type="email"
                  placeholder={t('common.emailPlaceholder')}
                  autoComplete="email"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold
                         hover:bg-pp-orange/90 transition-colors disabled:opacity-50
                         flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('resetPassword.sendLink')
              )}
            </button>
          </form>

          {/* Back to Login Link */}
          <Link
            to="/login"
            className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-pp-orange hover:text-pp-orange/80"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('resetPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
